import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { normalizeHistoryData } from "../utils/normalizeHistoryData";
import { getApiBase, getN8nWebhookUrl } from "../utils/config";
import "./ChallanForm.css";

const API_BASE = getApiBase();

// Avoid large section payloads being dropped by default limits (proxy/body-parser)
if (typeof axios.defaults !== "undefined") {
  axios.defaults.maxBodyLength = Infinity;
  axios.defaults.maxContentLength = Infinity;
}

// Convert keys at all levels from underscore to space (for display only)
function keysToSpaces(obj) {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((item) => keysToSpaces(item));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const displayKey = String(k).replace(/_/g, " ");
    out[displayKey] = keysToSpaces(v);
  }
  return out;
}

// Convert keys at all levels from space to underscore (for internal use and save)
function keysToUnderscores(obj) {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((item) => keysToUnderscores(item));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const internalKey = String(k).replace(/ /g, "_");
    out[internalKey] = keysToUnderscores(v);
  }
  return out;
}

// n8n response key -> backend section key (for table-wise save)
const N8N_KEY_TO_SECTION = {
  history_of_challans: "historyOfChallan",
  history_of_hypothecations: "historyOfHypothecation",
  history_of_insurances: "historyOfInsurance",
  history_of_paid_fees: "historyOfPaidFees",
  history_of_road_taxes: "historyOfRoadTax",
  history_of_transfer_ownerships: "historyOfOwnership",
  history_of_blacklists: "historyOfBlacklist",
  history_of_permits: "historyOfPermit",
  history_of_tax_clears: "historyOfTaxClear",
  history_of_fitness: "historyOfFitness",
  history_of_changed_vehicle_record_by_admins: "historyOfChangedVehicle",
  history_of_rc_print_details: "historyOfRcPrint",
};

// Sections that require Sr_No / Serial_No in every row (mandatory)
const SECTIONS_REQUIRING_SR_NO = [
  "historyOfChallan",
  "historyOfInsurance",
  "historyOfBlacklist",
  "historyOfPermit",
  "historyOfTaxClear",
  "historyOfChangedVehicle",
];
const SR_NO_KEYS = ["Sr_No", "SrNo", "sr_no", "srno", "serialNo", "Serial_No", "Serial_no", "SerialNo"];

function rowHasValidSrNo(row) {
  const val = SR_NO_KEYS.map((k) => row[k]).find((v) => v != null && String(v).trim() !== "");
  return val != null;
}

const ChallanForm = ({ notify }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [savingSections, setSavingSections] = useState(new Set());
  const [isSavingAll, setIsSavingAll] = useState(false);
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Split pane sizing
  const DEFAULT_LEFT = 380;
  const MIN_LEFT = 280;
  const MIN_RIGHT = 360;
  const [leftWidth, setLeftWidth] = useState(() => {
    const saved = Number(localStorage.getItem("split_left_width") || "");
    return Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_LEFT;
  });

  const [registrationNo, setRegistrationNo] = useState("");
  const [challanData, setChallanData] = useState("");
  const [hypothecationData, setHypothecationData] = useState("");
  const [insuranceData, setInsuranceData] = useState("");
  const [paidFeesData, setPaidFeesData] = useState("");
  const [roadTaxData, setRoadTaxData] = useState("");
  const [ownershipData, setOwnershipData] = useState("");
  const [blacklistData, setBlacklistData] = useState("");
  const [permitData, setPermitData] = useState("");
  const [taxClearData, setTaxClearData] = useState("");
  const [fitnessData, setFitnessData] = useState("");
  const [changedVehicleData, setChangedVehicleData] = useState("");
  const [rcPrintData, setRcPrintData] = useState("");

  const [n8nJsonData, setN8nJsonData] = useState(null);
  const [copied, setCopied] = useState(false);

  // Cooldown after last edit so all fields are synced before submit (2 seconds).
  const SUBMIT_COOLDOWN_SECONDS = 2;
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const isFirstInputChange = useRef(true);

  useEffect(() => {
    if (isFirstInputChange.current) {
      isFirstInputChange.current = false;
      return;
    }
    setCooldownSeconds(SUBMIT_COOLDOWN_SECONDS);
  }, [
    challanData,
    hypothecationData,
    insuranceData,
    paidFeesData,
    roadTaxData,
    ownershipData,
    blacklistData,
    permitData,
    taxClearData,
    fitnessData,
    changedVehicleData,
    rcPrintData,
  ]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const id = setInterval(() => {
      setCooldownSeconds((prev) => (prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownSeconds]);

  const canSubmit = !isProcessing && cooldownSeconds <= 0;

  // VALUE refs: updated in onChange so we always have latest content at submit (controlled inputs
  // make DOM .value === state, so state/DOM can be stale; these refs are written synchronously on every keystroke).
  const refChallanVal = useRef("");
  const refHypothecationVal = useRef("");
  const refInsuranceVal = useRef("");
  const refPaidFeesVal = useRef("");
  const refRoadTaxVal = useRef("");
  const refOwnershipVal = useRef("");
  const refBlacklistVal = useRef("");
  const refPermitVal = useRef("");
  const refTaxClearVal = useRef("");
  const refFitnessVal = useRef("");
  const refChangedVehicleVal = useRef("");
  const refRcPrintVal = useRef("");

  const STATE_KEYS = [
    "challanData", "hypothecationData", "insuranceData", "paidFeesData", "roadTaxData", "ownershipData",
    "blacklistData", "permitData", "taxClearData", "fitnessData", "changedVehicleData", "rcPrintData",
  ];

  const VALUE_REF_CONFIG = [
    [refChallanVal, "history_of_challans", "history_of_challans"],
    [refHypothecationVal, "history_of_hypothecations", "history_of_hypothecations"],
    [refInsuranceVal, "history_of_insurances", "history_of_insurances"],
    [refPaidFeesVal, "history_of_paid_fees", "history_of_paid_fees"],
    [refRoadTaxVal, "history_of_road_taxes", "history_of_road_taxes"],
    [refOwnershipVal, "history_of_transfer_ownerships", "history_of_transfer_ownerships"],
    [refBlacklistVal, "history_of_blacklists", "history_of_blacklists"],
    [refPermitVal, "history_of_permits", "history_of_permits"],
    [refTaxClearVal, "history_of_tax_clears", "history_of_tax_clears"],
    [refFitnessVal, "history_of_fitness", "history_of_fitness"],
    [refChangedVehicleVal, "history_of_changed_vehicle_record_by_admins", "history_of_changed_vehicle_record_by_admins"],
    [refRcPrintVal, "history_of_rc_print_details", "history_of_rc_print_details"],
  ];

  // Sync value refs from state when state changes (so paste/undo also updates refs)
  useEffect(() => {
    refChallanVal.current = challanData;
    refHypothecationVal.current = hypothecationData;
    refInsuranceVal.current = insuranceData;
    refPaidFeesVal.current = paidFeesData;
    refRoadTaxVal.current = roadTaxData;
    refOwnershipVal.current = ownershipData;
    refBlacklistVal.current = blacklistData;
    refPermitVal.current = permitData;
    refTaxClearVal.current = taxClearData;
    refFitnessVal.current = fitnessData;
    refChangedVehicleVal.current = changedVehicleData;
    refRcPrintVal.current = rcPrintData;
  }, [
    challanData,
    hypothecationData,
    insuranceData,
    paidFeesData,
    roadTaxData,
    ownershipData,
    blacklistData,
    permitData,
    taxClearData,
    fitnessData,
    changedVehicleData,
    rcPrintData,
  ]);

  /**
   * Build list from VALUE refs (updated in onChange + useEffect). Using state or DOM .value
   * caused only 6 API calls: React 18 batches updates, so state/DOM often had only first 6
   * sections when Submit ran; value refs are written on every change so all 12 are available.
   */
  const getSectionsToProcess = () => {
    const list = [];
    for (const [valueRef, header, mergeKey] of VALUE_REF_CONFIG) {
      const value = String(valueRef.current ?? "").trim();
      if (!value) continue;
      const tableNameWithSpaces = header.replace(/_/g, " ");
      list.push({ text: `${tableNameWithSpaces}\n${value}`, mergeKey });
    }
    return list;
  };

  // Normalize n8n JSON response using utility function
  const normalizedData = useMemo(() => {
    return normalizeHistoryData(n8nJsonData);
  }, [n8nJsonData]);

  const jsonText = useMemo(() => {
    if (!n8nJsonData) return "";
    const forDisplay = keysToSpaces({ registration_no: registrationNo, ...n8nJsonData });
    return JSON.stringify(forDisplay, null, 2);
  }, [n8nJsonData, registrationNo]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
      notify?.("JSON copied to clipboard", "success");
    } catch (e) {
      notify?.("Copy failed", e?.message);
    }
  };

  // Parse n8n response (supports: content[].text, output[0].content[0].text, or data.text)
  const parseN8nResponse = (res) => {
    const data = Array.isArray(res.data) ? res.data[0] : res.data;
    let outputText =
      data?.output?.[0]?.content?.[0]?.text ||
      data?.text;
    if (!outputText && Array.isArray(data?.content)) {
      const textItem = data.content.find((c) => c.type === "output_text" || c.type === "text");
      outputText = textItem?.text ?? data.content[0]?.text;
    }
    if (!outputText) throw new Error("No output text from n8n");
    const jsonMatch = outputText.match(/```json\s*([\s\S]*?)\s*```/) || outputText.match(/{[\s\S]*}/);
    const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : outputText;
    return JSON.parse(jsonStr.trim());
  };

  // Submit: call n8n once per input; update live preview as each completes; save only after all done
  const handleSubmit = async () => {
    if (!registrationNo.trim()) {
      notify?.("Registration Number is required", "warning");
      return;
    }

    const sections = getSectionsToProcess();

    // Diagnostic: why only N n8n API calls (React state vs value refs)
    const stateValues = {
      challanData,
      hypothecationData,
      insuranceData,
      paidFeesData,
      roadTaxData,
      ownershipData,
      blacklistData,
      permitData,
      taxClearData,
      fitnessData,
      changedVehicleData,
      rcPrintData,
    };
    const perSection = VALUE_REF_CONFIG.map(([valueRef, , mergeKey], i) => {
      const stateLen = (stateValues[STATE_KEYS[i]] ?? "").length;
      const valueRefLen = (valueRef.current ?? "").length;
      const value = String(valueRef.current ?? "").trim();
      return { mergeKey, stateLen, valueRefLen, included: value.length > 0 };
    });
    const reason =
      sections.length < 12
        ? "Only " +
          sections.length +
          " sections have content in value refs at submit time. If you filled more: React may have batched state so state/dom were stale; value refs are updated in onChange so they should have latest. Check perSection: included=false means valueRef was empty."
        : "All " + sections.length + " filled sections included (value refs had content).";

    console.log("[n8n] WHY ONLY N API CALLS — sections to send:", sections.length, "list:", sections.map((s) => s.mergeKey));
    console.log("[n8n] REASON:", reason);
    console.log("[n8n] Per section (stateLen vs valueRefLen, included):", perSection);

    const requestId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      await axios.post(`${API_BASE}/challan/log-n8n-submit`, {
        requestId,
        sectionCount: sections.length,
        sections: sections.map((s) => s.mergeKey),
        reason,
        perSection,
      });
    } catch (e) {
      console.warn("[n8n] Backend log failed:", e?.message);
    }

    if (sections.length === 0) {
      notify?.("Please enter at least one history section", "warning");
      return;
    }

    setIsProcessing(true);
    setN8nJsonData(null);
    try {
      const n8nWebhookUrl =
        getN8nWebhookUrl() ||
        (await axios.get(`${API_BASE}/challan/config`).then((r) => r?.data?.n8nWebhookUrl?.trim()));
      if (!n8nWebhookUrl) {
        notify?.("N8N webhook URL is not configured. Set VITE_N8N_WEBHOOK_URL in frontend .env or N8N_WEBHOOK_URL in backend .env", "error");
        setIsProcessing(false);
        return;
      }

      const promises = sections.map((section) => {
        console.log("➡️ sending", section.mergeKey, `[requestId=${requestId}]`);
        return axios
          .post(n8nWebhookUrl, section.text, {
            headers: {
              "Content-Type": "text/plain",
              "X-Request-Id": requestId,
            },
            maxBodyLength: Infinity,
            maxContentLength: Infinity,
          })
          .then((res) => {
            console.log("✅ sent", section.mergeKey, `[requestId=${requestId}]`);
            return parseN8nResponse(res);
          })
          .catch((e) => {
            console.error("❌ failed", section.mergeKey, e?.message || String(e), `[requestId=${requestId}]`);
            throw e;
          });
      });

      // Wait for ALL nodes to return (success or fail) before showing any output.
      // Merge by section: each response goes under its section's mergeKey so we don't overwrite sections.
      const settled = await Promise.allSettled(promises);
      const merged = {};
      const failed = [];
      for (let i = 0; i < settled.length; i++) {
        const s = settled[i];
        const mergeKey = sections[i].mergeKey;
        const keyWithSpaces = mergeKey.replace(/_/g, " ");
        if (s.status === "fulfilled") {
          const res = s.value || {};
          const sectionData = res[mergeKey] ?? res[keyWithSpaces];
          // n8n may return a single object instead of an array; normalize to array for tables
          const asArray = Array.isArray(sectionData)
            ? sectionData
            : sectionData != null && typeof sectionData === "object"
              ? [sectionData]
              : [];
          merged[mergeKey] = asArray;
          if (res["registration no"] !== undefined) merged["registration_no"] = res["registration no"];
          if (res["registration_no"] !== undefined) merged["registration_no"] = res["registration_no"];
        } else {
          const reason = s.reason?.message || String(s.reason);
          failed.push({ sectionIndex: i + 1, mergeKey, reason });
          merged[mergeKey] = [];
          console.error("❌ section rejected (allSettled)", mergeKey, reason);
        }
      }
      // n8n may return keys with spaces; convert all keys (including inside arrays) to underscores for normalize/save
      const normalizedKeys = {};
      for (const [k, v] of Object.entries(merged)) {
        const key = k.replace(/ /g, "_");
        normalizedKeys[key] = keysToUnderscores(v);
      }
      setN8nJsonData(normalizedKeys);
      if (failed.length === 0) {
        notify?.(`All ${sections.length} section${sections.length > 1 ? "s" : ""} converted. Review and save all tables.`, "success");
      } else if (Object.keys(merged).length > 0) {
        notify?.(`${sections.length - failed.length}/${sections.length} sections converted. ${failed.length} failed.`, "warning");
      } else {
        const msg = failed[0]?.reason || "n8n conversion failed";
        notify?.(`Conversion failed: ${msg}`, "error");
      }
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || "n8n conversion failed";
      notify?.(`Conversion failed: ${msg}`, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  // Save all tables at once (after all processing complete)
  const handleSaveAll = async () => {
    if (!n8nJsonData || !normalizedData) {
      notify?.("No converted data to save. Submit the form first.", "warning");
      return;
    }
    if (!registrationNo.trim()) {
      notify?.("Registration Number is required.", "warning");
      return;
    }
    setIsSavingAll(true);
    try {
      const token = localStorage.getItem("token") || "";
      const payload = {
        registrationNo: registrationNo.trim(),
        isAiConverted: true,
        historyOfChallan: normalizedData.historyOfChallan || [],
        historyOfHypothecation: normalizedData.historyOfHypothecation || [],
        historyOfInsurance: normalizedData.historyOfInsurance || [],
        historyOfPaidFees: normalizedData.historyOfPaidFees || [],
        historyOfRoadTax: normalizedData.historyOfRoadTax || [],
        historyOfOwnership: normalizedData.historyOfOwnership || [],
        historyOfBlacklist: normalizedData.historyOfBlacklist || [],
        historyOfPermit: normalizedData.historyOfPermit || [],
        historyOfTaxClear: normalizedData.historyOfTaxClear || [],
        historyOfFitness: normalizedData.historyOfFitness || [],
        historyOfChangedVehicle: normalizedData.historyOfChangedVehicle || [],
        historyOfRcPrint: normalizedData.historyOfRcPrint || [],
      };
      const res = await axios.post(`${API_BASE}/challan/save`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = res?.data || {};
      const errList = data.errors || [];
      if (errList.length > 0) {
        const msg = data.message || `Saved with ${errList.length} error(s).`;
        notify?.(msg, "error");
        console.error("Save errors (check backend logs for details):", errList);
        errList.forEach((e, i) => console.error(`  [${i + 1}] ${e.type}:`, e.error, e.receipt ?? e.serial_no ?? e.application ?? e.applicationNo ?? e.sr_no ?? e.appl_no ?? ""));
      } else {
        notify?.(data.message || "All tables saved successfully!", "success");
        setChallanData("");
        setHypothecationData("");
        setInsuranceData("");
        setPaidFeesData("");
        setRoadTaxData("");
        setOwnershipData("");
        setBlacklistData("");
        setPermitData("");
        setTaxClearData("");
        setFitnessData("");
        setChangedVehicleData("");
        setRcPrintData("");
        setN8nJsonData(null);
        setRegistrationNo("");
      }
    } catch (err) {
      console.error(err);
      notify?.(`Save failed: ${err.response?.data?.message || err.message}`, "error");
    } finally {
      setIsSavingAll(false);
    }
  };

  // Section keys and display labels for table cards
  const SECTION_ORDER = [
    "historyOfChallan", "historyOfHypothecation", "historyOfInsurance", "historyOfPaidFees",
    "historyOfRoadTax", "historyOfOwnership", "historyOfBlacklist", "historyOfPermit",
    "historyOfTaxClear", "historyOfFitness", "historyOfChangedVehicle", "historyOfRcPrint",
  ];
  const SECTION_LABELS = {
    historyOfChallan: "History Of Challans",
    historyOfHypothecation: "History Of Hypothecations",
    historyOfInsurance: "History Of Insurances",
    historyOfPaidFees: "History Of Paid Fees",
    historyOfRoadTax: "History Of Road Taxes",
    historyOfOwnership: "History Of Transfer Ownerships",
    historyOfBlacklist: "History Of Blacklists",
    historyOfPermit: "History Of Permits",
    historyOfTaxClear: "History Of Tax Clears",
    historyOfFitness: "History Of Fitness",
    historyOfChangedVehicle: "History Of Changed Vehicle Record By Admins",
    historyOfRcPrint: "History Of Rc Print Details",
  };
  const FORM_FIELD_LABELS = {
    registrationNumber: "Registration Number",
    historyOfChallans: "History Of Challans",
    historyOfHypothecations: "History Of Hypothecations",
    historyOfInsurances: "History Of Insurances",
    historyOfPaidFees: "History Of Paid Fees",
    historyOfRoadTaxes: "History Of Road Taxes",
    historyOfTransferOwnerships: "History Of Transfer Ownerships",
    historyOfBlacklists: "History Of Blacklists",
    historyOfPermits: "History Of Permits",
    historyOfTaxClears: "History Of Tax Clears",
    historyOfFitness: "History Of Fitness",
    historyOfChangedVehicleRecordByAdmins: "History Of Changed Vehicle Record By Admins",
    historyOfRcPrintDetails: "History Of Rc Print Details",
  };


  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingRef.current) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const total = rect.width;
      const nextLeft = Math.max(MIN_LEFT, Math.min(x, total - MIN_RIGHT));
      setLeftWidth(nextLeft);
    };

    const onUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      localStorage.setItem("split_left_width", String(leftWidth));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [leftWidth]);

  const startDrag = () => {
    isDraggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  return (
    <div className="challan-container" ref={containerRef}>
      {/* LEFT SIDE: FORM */}
      <div className="form-side" style={{ width: leftWidth }}>
        <h2 style={{ margin: "0 0 20px 0", color: "#333" }}>AI Parser</h2>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.registrationNumber}</b>
          </label>
          <input
            type="text"
            className="form-input"
            value={registrationNo}
            onChange={(e) => setRegistrationNo(e.target.value)}
            placeholder="e.g. RJ52GA6055, DL01CA9999"
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfChallans}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={6}
            value={challanData}
            onChange={(e) => {
              const v = e.target.value;
              refChallanVal.current = v;
              setChallanData(v);
            }}
            placeholder="Paste challan history table or text here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfHypothecations}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={hypothecationData}
            onChange={(e) => {
              const v = e.target.value;
              refHypothecationVal.current = v;
              setHypothecationData(v);
            }}
            placeholder="Paste hypothecation history here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfInsurances}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={insuranceData}
            onChange={(e) => {
              const v = e.target.value;
              refInsuranceVal.current = v;
              setInsuranceData(v);
            }}
            placeholder="Paste insurance history here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfPaidFees}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={paidFeesData}
            onChange={(e) => {
              const v = e.target.value;
              refPaidFeesVal.current = v;
              setPaidFeesData(v);
            }}
            placeholder="Paste paid fees history here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfRoadTaxes}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={roadTaxData}
            onChange={(e) => {
              const v = e.target.value;
              refRoadTaxVal.current = v;
              setRoadTaxData(v);
            }}
            placeholder="Paste road tax history here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfTransferOwnerships}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={ownershipData}
            onChange={(e) => {
              const v = e.target.value;
              refOwnershipVal.current = v;
              setOwnershipData(v);
            }}
            placeholder="Paste transfer ownership history here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfBlacklists}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={blacklistData}
            onChange={(e) => {
              const v = e.target.value;
              refBlacklistVal.current = v;
              setBlacklistData(v);
            }}
            placeholder="Paste blacklist data here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfPermits}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={permitData}
            onChange={(e) => {
              const v = e.target.value;
              refPermitVal.current = v;
              setPermitData(v);
            }}
            placeholder="Paste permit history here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfTaxClears}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={taxClearData}
            onChange={(e) => {
              const v = e.target.value;
              refTaxClearVal.current = v;
              setTaxClearData(v);
            }}
            placeholder="Paste tax clear data here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfFitness}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={fitnessData}
            onChange={(e) => {
              const v = e.target.value;
              refFitnessVal.current = v;
              setFitnessData(v);
            }}
            placeholder="Paste fitness history here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfChangedVehicleRecordByAdmins}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={changedVehicleData}
            onChange={(e) => {
              const v = e.target.value;
              refChangedVehicleVal.current = v;
              setChangedVehicleData(v);
            }}
            placeholder="Paste changed vehicle record data here..."
          />
        </div>

        <div className="input-group">
          <label className="form-label">
            <b>{FORM_FIELD_LABELS.historyOfRcPrintDetails}</b>
          </label>
          <textarea
            className="form-textarea"
            rows={4}
            value={rcPrintData}
            onChange={(e) => {
              const v = e.target.value;
              refRcPrintVal.current = v;
              setRcPrintData(v);
            }}
            placeholder="Paste RC print details here..."
          />
        </div>

        <div className="submit-cooldown-message" role="status" aria-live="polite">
          {isProcessing ? (
            <span className="cooldown-wait">Processing… Please wait.</span>
          ) : cooldownSeconds > 0 ? (
            <span className="cooldown-wait">
              Please wait <strong>{cooldownSeconds}</strong> second{cooldownSeconds !== 1 ? "s" : ""} after your last edit before submitting (ensures all fields are synced).
            </span>
          ) : (
            <span className="cooldown-ready">Ready to submit. We read all 12 fields from the form so every filled section is sent to n8n.</span>
          )}
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          className="submit-btn"
          disabled={!canSubmit}
        >
          {isProcessing ? "Processing..." : "Submit / Convert"}
        </button>
      </div>

      {/* DRAGGABLE SPLITTER */}
      <div className="splitter" onMouseDown={startDrag} />

      {/* RIGHT SIDE: Tables + JSON preview */}
      <div className="preview-side">
        <div className="preview-header">
          <h3>Live Preview — Tables &amp; JSON</h3>
          {n8nJsonData && (
            <button type="button" className="copy-json-btn" onClick={copyJson}>
              {copied ? "Copied" : "Copy JSON"}
            </button>
          )}
        </div>

        {isProcessing ? (
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <div className="loading-text">
              <h4>Processing your data...</h4>
              <p>Waiting for all sections to return. Output will show only when every node has finished as it’s ready</p>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        ) : !n8nJsonData ? (
          <p className="no-data-msg">
            Submit the form to see tables and JSON here. Each table is saved to the database as soon as its data is ready.
          </p>
        ) : (
          <>
            {/* Tables side by side (per section) */}
            <div className="tables-grid">
              {SECTION_ORDER.map((sectionKey) => {
                const rows = normalizedData?.[sectionKey];
                if (!Array.isArray(rows) || rows.length === 0) return null;
                const firstKeys = Object.keys(rows[0] || {});
                const otherKeys = rows.slice(1).flatMap((r) => Object.keys(r || {}));
                const headers = [...new Set([...firstKeys, ...otherKeys])];
                const isSaving = savingSections.has(sectionKey);
                const handleSaveTable = () => {
                  if (!registrationNo.trim()) {
                    notify?.("Registration Number is required.", "warning");
                    return;
                  }
                  if (SECTIONS_REQUIRING_SR_NO.includes(sectionKey)) {
                    const badRow = rows.findIndex((row) => !rowHasValidSrNo(row));
                    if (badRow !== -1) {
                      notify?.(`Sr.No / Serial_No is required for every row. Row ${badRow + 1} is missing it.`, "error");
                      return;
                    }
                  }
                  setSavingSections((prev) => new Set(prev).add(sectionKey));
                  const token = localStorage.getItem("token") || "";
                  axios
                    .post(
                      `${API_BASE}/challan/save-section`,
                      { registrationNo: registrationNo.trim(), section: sectionKey, data: rows },
                      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                    )
                    .then(() => {
                      setSavingSections((prev) => {
                        const next = new Set(prev);
                        next.delete(sectionKey);
                        return next;
                      });
                      notify?.(`${SECTION_LABELS[sectionKey] || sectionKey} saved.`, "success");
                    })
                    .catch((err) => {
                      setSavingSections((prev) => {
                        const next = new Set(prev);
                        next.delete(sectionKey);
                        return next;
                      });
                      notify?.(`Save failed: ${err.response?.data?.message || err.message}`, "error");
                    });
                };
                return (
                  <div key={sectionKey} className="table-card">
                    <div className="table-card-header">
                      <span className="table-card-title">{SECTION_LABELS[sectionKey] || sectionKey}</span>
                      <div className="table-card-actions">
                        {isSaving && <span className="table-saving-badge">Saving…</span>}
                        <button type="button" className="table-save-btn" onClick={handleSaveTable} disabled={isSaving}>
                          {isSaving ? "Saving…" : "Save table"}
                        </button>
                      </div>
                    </div>
                    <div className="table-card-body">
                      <table className="data-table">
                        <thead>
                          <tr>
                            {headers.map((h) => (
                              <th key={h}>{h.replace(/_/g, " ")}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i}>
                              {headers.map((h) => (
                                <td key={h}>{row[h] != null ? String(row[h]) : ""}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
            <details className="json-details" style={{ marginTop: 16 }}>
              <summary>JSON</summary>
              <pre className="json-panel">{jsonText}</pre>
            </details>
            <div className="save-all-row">
              <button
                type="button"
                className="save-all-btn"
                onClick={handleSaveAll}
                disabled={isSavingAll}
              >
                {isSavingAll ? "Saving all tables…" : "Save all tables"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChallanForm;
